import { fetchVariantsByIds, fetchVariantsByProductRef } from '../api/ARSCatalogApi';
import { sortVariantsByStatus, isNewItem } from '../utils/ARSCatalogUtils';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTooltip from '../../components/Components/Modals/CustomTooltip';
import HoverImageZoom from '../../components/Components/Images/HoverImageZoom';
import NewBadge from '../../components/Components/Badges/NewBadge';

import dayjs from 'dayjs';
import { variantTokens } from '../../theme/theme';

// Definizione delle colonne per la tabella dei prodotti
export const getProductColumns = (processImageFunction, processedImages, onProductClick) => [
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>Image</span>
                <CustomTooltip title="Image of the product">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'imgUrl',
        key: 'imgUrl',
        render: (text, record) => {
            const imgSrc = processedImages[record.id] || text;

            if (!processedImages[record.id] && text) {
                processImageFunction(text, record.id);
            }

            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    {imgSrc ? (
                        <HoverImageZoom
                            src={imgSrc}
                            alt="Product"
                            style={{
                                width: '3.5rem',
                                height: '3.5rem',
                                objectFit: 'contain',
                                display: 'block'
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '3.5rem',
                            height: '3.5rem',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: '#999',
                            fontSize: '0.75rem'
                        }}>
                            No Image
                        </div>
                    )}
                </div>
            );
        },
        align: 'center',
        width: '10%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <span>Product Name</span>
                <CustomTooltip title="Name of the product">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'modelName',
        key: 'modelName',
        render: (text, record) => {
            const shouldShowBadge = isNewItem(record);

            return (
                <NewBadge showBadge={shouldShowBadge}>
                    <span
                        style={{
                            color: variantTokens.accent,
                            textDecoration: 'none',
                            cursor: 'pointer'
                        }}
                        onClick={() => {
                            if (onProductClick) {
                                onProductClick(record.id);
                            } else {
                                // Fallback al comportamento originale
                                const event = new CustomEvent('saveFiltersBeforeNavigation');
                                window.dispatchEvent(event);
                                window.location.href = `/ars-catalog/product/${record.id}`;
                            }
                        }}
                    >
                        {text}
                    </span>
                </NewBadge>
            );
        },
        align: 'left',
        width: '30%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span>Variants</span>
                <CustomTooltip title="Number of available variants">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'variants_map',
        key: 'variantsCount',
        render: (variants_map) => {
            const count = variants_map ? variants_map.length : 0;
            return <div style={{ paddingRight: '1rem' }}>{count}</div>;
        },
        sorter: (a, b) => (a.variants_map?.length || 0) - (b.variants_map?.length || 0),
        align: 'right',
        width: '10%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <span>Brand</span>
                <CustomTooltip title="Associated brand">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'brandName',
        key: 'brandName',
        sorter: (a, b) => a.brandName.localeCompare(b.brandName),
        align: 'left',
        width: '30%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <span>Last Update</span>
                <CustomTooltip title="Date of last update">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'lastUpdate',
        key: 'lastUpdate',
        render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        sorter: (a, b) => (a.lastUpdate || '').localeCompare(b.lastUpdate || ''),
        align: 'left',
        width: '15%',
    },
];

// Definizione delle colonne per la tabella delle varianti
// Ordine richiesto: Image, Product Name, SKU, EAN, Size, Frame Color, Lenses Color
export const getVariantColumns = (processImageFunction, processedImages) => [
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>Image</span>
                <CustomTooltip title="Image of the variant">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'poster',
        key: 'poster',
        render: (text, variant) => {
            const variantImgKey = `variant_${variant.id}`;
            const imgSrc = processedImages[variantImgKey] || text;

            if (!processedImages[variantImgKey] && text) {
                processImageFunction(text, variantImgKey);
            }

            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    {imgSrc ? (
                        <HoverImageZoom
                            src={imgSrc}
                            alt="Variant"
                            style={{
                                width: '3.5rem',
                                height: '3.5rem',
                                objectFit: 'contain',
                                display: 'block'
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '3.5rem',
                            height: '3.5rem',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: '#999',
                            fontSize: '0.75rem'
                        }}>
                            No Image
                        </div>
                    )}
                </div>
            );
        },
        align: 'center',
        width: '8%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <span>Product Name</span>
                <CustomTooltip title="Name of the product">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'glassesName',
        key: 'glassesName',
        render: (text, variant) => (
            <NewBadge showBadge={isNewItem(variant)}>
                <span>{text}</span>
            </NewBadge>
        ),
        align: 'left',
        width: '15%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span>SKU</span>
                <CustomTooltip title="SKU of the model">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'skuModel',
        key: 'skuModel',
        align: 'right',
        width: '12%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span>EAN</span>
                <CustomTooltip title="EAN code of the variant">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'eanCode',
        key: 'eanCode',
        render: (text) => text || '-',
        align: 'right',
        width: '12%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span>Size</span>
                <CustomTooltip title="Size of the variant">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'size',
        key: 'size',
        render: (size) => {
            const sizeValue = size?.size || '-';
            return <div style={{ paddingRight: '1rem' }}>{sizeValue}</div>;
        },
        align: 'right',
        width: '8%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <span>Frame Color</span>
                <CustomTooltip title="Color of the frame">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'frameColor',
        key: 'frameColor',
        align: 'left',
        width: '10%',
    },
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <span>Lenses Color</span>
                <CustomTooltip title="Color of the lenses">
                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                </CustomTooltip>
            </div>
        ),
        dataIndex: 'lensesColor',
        key: 'lensesColor',
        render: (text) => text === "0" || !text ? "TRANSPARENT" : text,
        align: 'left',
        width: '10%',
    },
];

// Funzione per caricare le varianti dalla cache o da Firebase
export const getVariantsWithCache = async (productId, list_variants = [], variantsCache) => {
    // Se abbiamo già le varianti in cache, usale
    if (variantsCache[productId]) {
        return variantsCache[productId];
    }

    try {
        let productVariants = [];

        // 🔧 FIXED: Se non ci sono varianti nella list_variants, prova comunque con productRef
        // per garantire il caricamento di tutte le varianti disponibili
        if (!list_variants || list_variants.length === 0) {
            console.warn(`No list_variants provided for product ${productId}, trying productRef fallback`);
            try {
                productVariants = await fetchVariantsByProductRef(productId);
                if (productVariants.length > 0) {
                    const sortedVariants = sortVariantsByStatus(productVariants);
                    return sortedVariants;
                }
            } catch (refError) {
                console.error(`Error loading variants by productRef for ${productId}:`, refError);
            }
            return [];
        }

        // Prima prova a caricare dalle list_variants
        try {
            productVariants = await fetchVariantsByIds(list_variants);

            // Se abbiamo ottenuto almeno alcune varianti, non provare con productRef
            if (productVariants.length > 0) {
                // Ordina le varianti per stato
                const sortedVariants = sortVariantsByStatus(productVariants);
                return sortedVariants;
            }
        } catch (variantError) {
            console.error(`Error loading variants by IDs for product ${productId}:`, variantError);

            // Se l'errore è "Too many outstanding requests" o "client is offline", 
            // attendere un po' e ritornare un array vuoto invece di procedere
            if (variantError.code === 'resource-exhausted' ||
                variantError.message.includes('offline')) {
                console.warn('Firebase connection issue, will try again later');
                return [];
            }
        }

        // 🔧 FIXED: Se non ci sono varianti dal metodo precedente, prova sempre con productRef
        // Rimossa la limitazione arbitraria di 3 varianti per garantire il caricamento completo
        if (productVariants.length === 0) {
            try {
                console.warn(`Falling back to productRef for product ${productId} (list_variants method failed)`);
                productVariants = await fetchVariantsByProductRef(productId);
            } catch (refError) {
                console.error(`Error loading variants by productRef for ${productId}:`, refError);
                return [];
            }
        }

        // Ordina le varianti per stato
        const sortedVariants = sortVariantsByStatus(productVariants);
        return sortedVariants;
    } catch (error) {
        console.error(`Error loading variants for product ${productId}:`, error);
        if (error.code === 'resource-exhausted') {
            console.warn('Too many Firebase requests. Consider reducing the number of concurrent requests or implementing rate limiting.');
        }
        return [];
    }
}; 